import React, { useState, useEffect } from 'react';
import { apiKeyAPI } from '../api/userAPI';
import { useAuth } from '../context/AuthContext';
import { ConfirmDialog, InlineNotice } from './shared/UIComponents';

const APIKeyManager = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [pendingRevokeId, setPendingRevokeId] = useState(null);
  const [notice, setNotice] = useState(null);

  const { user } = useAuth();

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      setLoading(true);
      setError(null);

      const keys = await apiKeyAPI.list();
      setApiKeys(keys);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (keyData) => {
    try {
      setCreating(true);
      setError(null);

      const response = await apiKeyAPI.create(keyData);
      setNewKey(response.api_key);
      setShowCreateForm(false);
      await loadAPIKeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      setError(null);
      await apiKeyAPI.revoke(keyId);
      await loadAPIKeys();
      setNotice({ type: 'success', message: 'API key revoked successfully.' });
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingRevokeId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setNotice({ type: 'success', message: 'API key copied to clipboard.' });
    }).catch(() => {
      setNotice({ type: 'error', message: 'Failed to copy to clipboard.' });
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="api-key-manager">
        <div className="loading">Loading API keys...</div>
      </div>
    );
  }

  return (
    <div className="api-key-manager">
      <div className="api-key-header">
        <h2>API Key Management</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          Create New API Key
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      <InlineNotice type={notice?.type} message={notice?.message} />

      {/* New Key Display */}
      {newKey && (
        <div className="new-key-display">
          <h3>New API Key Created</h3>
          <div className="key-info">
            <p><strong>Name:</strong> {newKey.name}</p>
            <p><strong>Key:</strong></p>
            <div className="key-value">
              <code>{newKey.key}</code>
              <button
                className="btn btn-sm btn-outline"
                onClick={() => copyToClipboard(newKey.key)}
              >
                Copy
              </button>
            </div>
            <div className="warning">
              <strong>Important:</strong> This is the only time you'll see this API key. 
              Make sure to copy and store it securely.
            </div>
          </div>
          <button
            className="btn btn-outline"
            onClick={() => setNewKey(null)}
          >
            Close
          </button>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <CreateAPIKeyForm
          onSubmit={handleCreateKey}
          onCancel={() => setShowCreateForm(false)}
          loading={creating}
        />
      )}

      {/* API Keys List */}
      <div className="api-keys-list">
        <h3>Your API Keys</h3>
        {apiKeys.length === 0 ? (
          <div className="empty-state">
            <p>No API keys found. Create your first API key to get started.</p>
          </div>
        ) : (
          <div className="keys-table">
            <div className="table-header">
              <div>Name</div>
              <div>Description</div>
              <div>Status</div>
              <div>Created</div>
              <div>Last Used</div>
              <div>Actions</div>
            </div>
            {apiKeys.map((key) => (
              <div key={key.id} className="table-row">
                <div className="key-name">{key.name}</div>
                <div className="key-description">{key.description || 'No description'}</div>
                <div className={`key-status status-${key.status}`}>
                  {key.status}
                </div>
                <div className="key-created">{formatDate(key.created_at)}</div>
                <div className="key-last-used">
                  {key.last_used ? formatDate(key.last_used) : 'Never'}
                </div>
                <div className="key-actions">
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setPendingRevokeId(key.id)}
                    disabled={key.status === 'revoked'}
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API Usage Instructions */}
      <div className="api-usage-info">
        <h3>Using Your API Keys</h3>
        <div className="usage-examples">
          <h4>Authentication</h4>
          <p>Include your API key in the Authorization header:</p>
          <pre><code>Authorization: Bearer YOUR_API_KEY</code></pre>

          <h4>Example Request</h4>
          <pre><code>{`curl -X GET "http://127.0.0.1:8000/omics/datasets" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</code></pre>

          <h4>Rate Limits</h4>
          <p>API calls are limited based on your subscription tier:</p>
          <ul>
            <li><strong>Free:</strong> 10 requests per minute</li>
            <li><strong>Basic:</strong> 50 requests per minute</li>
            <li><strong>Professional:</strong> 200 requests per minute</li>
            <li><strong>Enterprise:</strong> 1000 requests per minute</li>
          </ul>
        </div>
      </div>
      <ConfirmDialog
        open={Boolean(pendingRevokeId)}
        title="Revoke API key?"
        message="This action cannot be undone."
        confirmLabel="Revoke"
        onConfirm={() => handleRevokeKey(pendingRevokeId)}
        onCancel={() => setPendingRevokeId(null)}
      />
    </div>
  );
};

// Create API Key Form Component
const CreateAPIKeyForm = ({ onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="create-key-form">
      <h3>Create New API Key</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={loading}
            placeholder="e.g., My App API Key"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={loading}
            placeholder="Optional description for this API key"
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-outline"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create API Key'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default APIKeyManager;
